/**
 * The first thing two peers say to each other, and the only thing they say before one of them is
 * allowed to exist.
 *
 * It carries a role claim, how long this side has held the code, and a nonce. The role claim is
 * what makes the star a star: a guest that hears `guest` refuses the pair outright, so the mesh a
 * room would otherwise build never forms.
 *
 * `heldMs` is what settles a collision between two hosts that meet late. Relays are slow enough
 * that two people can both believe they own a code before either hears the other, and when that
 * happens the one who has held it longer keeps it. It is a duration rather than a timestamp, so no
 * clock anywhere has to agree with any other.
 *
 * The nonce is there for the log, so two attempts from the same peer id can be told apart when a
 * connection flaps.
 *
 * `freeSeats` is how the four-player cap is refused without a round trip. A refused handshake
 * destroys the connection and carries no reason with it, so a guest that is about to be turned away
 * has to reach that verdict itself, from the number the host puts here. A guest sends zero, because
 * a guest seats nobody.
 *
 * `seatProof` is what stops that count turning a returning player away from its own chair. A guest
 * that was seated here before proves it over the nonce above, and the host counts the seat it is
 * keeping for that guest as free to exactly that guest. Zeroes mean nothing is claimed, which is
 * what a host and a first-time guest both send. See [seatProof](./seatProof.ts) for why a digest
 * goes on the wire and the token never does.
 *
 * The version is not repeated here. It rides the frame header, where the codec checks it before
 * this payload is ever looked at.
 *
 * ```text
 * byte 0   role claim
 * byte 1-4 held milliseconds, little endian
 * byte 5-8 nonce, little endian
 * byte 9   free seats
 * byte 10-17 seat claim, a digest, zero when nothing is claimed
 * ```
 */

import { NetError } from '@/net/session/sessionErrors';
import { SEAT_PROOF_BYTES } from '@/net/wire/seatProof';

/**
 * `hostProbing` is a host that has generated a code and has not yet waited out its claim window.
 * It is a distinct claim because two probing hosts settle a collision differently from a probing
 * host meeting a settled one.
 */
export type RoleClaim = 'hostProbing' | 'host' | 'guest';

/** The fixed head, before the claim: role, held time, nonce, free seats. */
const PROOF_AT = 10;

export const HELLO_BYTES = PROOF_AT + SEAT_PROOF_BYTES;

const ROLE_CODES = { hostProbing: 1, host: 2, guest: 3 } as const satisfies Record<
  RoleClaim,
  number
>;

const ROLE_BY_CODE: ReadonlyMap<number, RoleClaim> = new Map(
  (Object.keys(ROLE_CODES) as RoleClaim[]).map((role) => [ROLE_CODES[role], role]),
);

export interface HelloMessage {
  readonly role: RoleClaim;
  /** How long this side has held its claim. Capped, because a session can run all day. */
  readonly heldMs: number;
  readonly nonce: number;
  /** Seats this side can still offer. Always zero from a guest, which seats nobody. */
  readonly freeSeats: number;
  /**
   * A returning guest's claim on a seat the host is keeping. `NO_SEAT_PROOF` says nothing is
   * claimed, which is what a host and a guest that has never sat here both send.
   */
  readonly seatProof: Uint8Array;
}

/** Four bytes of milliseconds is 49 days. Anything longer compares the same either way. */
const MAX_HELD_MS = 0xffffffff;

export function encodeHello(message: HelloMessage): Uint8Array {
  const bytes = new Uint8Array(HELLO_BYTES);
  bytes[0] = ROLE_CODES[message.role];
  const view = new DataView(bytes.buffer);
  view.setUint32(1, Math.min(Math.max(Math.round(message.heldMs), 0), MAX_HELD_MS), true);
  view.setUint32(5, message.nonce >>> 0, true);
  bytes[9] = Math.min(Math.max(Math.round(message.freeSeats), 0), 0xff);
  bytes.set(message.seatProof.slice(0, SEAT_PROOF_BYTES), PROOF_AT);
  return bytes;
}

/** Throws rather than defaulting a role, because a defaulted role is how a mesh gets built. */
export function decodeHello(payload: Uint8Array): HelloMessage {
  if (payload.byteLength < HELLO_BYTES) {
    throw new NetError('malformedFrame', `a hello is ${String(HELLO_BYTES)} bytes`);
  }

  const role = ROLE_BY_CODE.get(payload[0] ?? 0);
  if (role === undefined) {
    throw new NetError(
      'malformedFrame',
      `role claim ${String(payload[0])} is not one this build sends`,
    );
  }

  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  return {
    role,
    heldMs: view.getUint32(1, true),
    nonce: view.getUint32(5, true),
    freeSeats: payload[9] ?? 0,
    seatProof: payload.slice(PROOF_AT, PROOF_AT + SEAT_PROOF_BYTES),
  };
}
